"use client";
import { useState, useEffect } from "react";
import { ArrowRight, Check, Clock, X, MessageSquare, Send, Edit, Star } from "lucide-react";
import apiClient from "@/services/api";

interface Activity {
  id: string;
  title: string;
  description: string;
  time: string;
  status: "sent" | "draft" | "pending" | "completed" | "failed";
  platform?: string;
  type: "review" | "response" | "integration" | "user";
}

export function ActivityList() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch real activity data
  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 Fetching recent activities...');
      const response = await apiClient.get('/backend/api/comments');

      // The API returns an array directly, not wrapped in success/data
      if (Array.isArray(response.data)) {
        const comments = response.data;

        // Transform comments into activity format
        const recentActivities: Activity[] = comments
          .slice(0, 6) // Get latest 6
          .map((comment: any, index: number) => {
            const timeAgo = comment.createdAt ?
              getTimeAgo(new Date(comment.createdAt)) :
              comment.time ? getTimeAgo(new Date(comment.time)) :
              'Recently';

            return {
              id: comment._id || `activity-${index}`,
              title: getActivityTitle(comment),
              description: getActivityDescription(comment),
              time: timeAgo,
              status: comment.status || 'pending',
              platform: comment.platform || 'Platform',
              type: 'review'
            };
          });

        setActivities(recentActivities);
        console.log('✅ Recent activities loaded:', recentActivities.length);
      } else if (response.data.success) {
        // Fallback for wrapped response
        const comments = response.data.data || [];
        const recentActivities: Activity[] = comments
          .slice(0, 6)
          .map((comment: any, index: number) => ({
            id: comment._id || `activity-${index}`,
            title: getActivityTitle(comment),
            description: getActivityDescription(comment),
            time: comment.createdAt ? getTimeAgo(new Date(comment.createdAt)) : 'Recently',
            status: comment.status || 'pending',
            platform: comment.platform || 'Platform',
            type: 'review'
          }));

        setActivities(recentActivities);
        console.log('✅ Recent activities loaded:', recentActivities.length);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      console.error('❌ Error fetching activities:', error);
      setError(error.message || 'Failed to load activities');

      // Fallback to empty array
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get activity title (same logic as response page)
  const getActivityTitle = (comment: any): string => {
    const status = comment.status || 'pending';

    if (status === 'sent') {
      return 'Response sent';
    } else if (status === 'draft') {
      return 'Response drafted';
    } else {
      return 'New review received';
    }
  };

  // Helper function to get activity description (same logic as response page)
  const getActivityDescription = (comment: any): string => {
    const customer = comment.reviewer_display_name || 'Customer';
    const product = comment.product_title || 'product';
    const platform = comment.platform || 'platform';
    const status = comment.status || 'pending';

    if (status === 'sent') {
      return `Response sent to ${customer} on ${platform}`;
    } else if (status === 'draft') {
      return `Response drafted for ${customer}'s review`;
    } else {
      return `${customer} left a review for ${product}`;
    }
  };

  // Helper function to calculate time ago
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-200 group-hover:scale-[1.02] group-hover:shadow-sm">
            <Send className="h-3 w-3" />
          </div>
        );
      case "draft":
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-200 group-hover:scale-[1.02] group-hover:shadow-sm">
            <Edit className="h-3 w-3" />
          </div>
        );
      case "pending":
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-200 group-hover:scale-[1.02] group-hover:shadow-sm">
            <Clock className="h-3 w-3" />
          </div>
        );
      case "completed":
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-200 group-hover:scale-[1.02] group-hover:shadow-sm">
            <Check className="h-3 w-3" />
          </div>
        );
      case "failed":
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-200 group-hover:scale-[1.02] group-hover:shadow-sm">
            <X className="h-3 w-3" />
          </div>
        );
      default:
        return (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 transition-all duration-200 group-hover:scale-[1.02] group-hover:shadow-sm">
            <MessageSquare className="h-3 w-3" />
          </div>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Activity</h3>
          <button className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            View all
          </button>
        </div>
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Activity</h3>
          <button
            onClick={fetchActivities}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Retry
          </button>
        </div>
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-2">{error}</p>
            <button
              onClick={fetchActivities}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recent Activity</h3>
        <button
          onClick={fetchActivities}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          Refresh
        </button>
      </div>
      <div className="space-y-2 flex-1">
        {activities.length === 0 ? (
          <div className="flex items-center justify-center flex-1 py-8">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No recent activity</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Activity will appear here when you start responding to reviews</p>
            </div>
          </div>
        ) : (
          activities.map((activity) => (
            <div
              key={activity.id}
              className={`group cursor-pointer rounded-lg border border-transparent p-2 transition-all duration-300 hover:border-gray-200 dark:hover:border-gray-800 ${
                expandedId === activity.id ? "bg-gray-50 dark:bg-gray-900" : ""
              }`}
              onClick={() => setExpandedId(expandedId === activity.id ? null : activity.id)}
            >
              <div className="flex items-start gap-3">
                {getStatusIcon(activity.status)}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 dark:text-white">{activity.title}</h4>
                    <span className="text-xs text-gray-600 dark:text-gray-400">{activity.time}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{activity.description}</p>

                  {expandedId === activity.id && (
                    <div className="mt-2 flex items-center justify-between rounded-lg bg-gray-100 dark:bg-gray-800 p-2 text-sm animate-fadeIn" style={{ animationDuration: '0.4s', animationTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)' }}>
                      <span className="text-gray-700 dark:text-gray-300">Platform: {activity.platform}</span>
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 text-gray-500 dark:text-gray-400" style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)' }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
